import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Compass,
  Sparkles,
  Quote,
  GraduationCap,
} from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";

const TEAM = [
  {
    name: "Michael",
    role: "Director",
    school: "Massey University, Auckland",
    body: "Michael created this team to fuse accounting, technology, and design. He believes a business needs a balance of these three elements to be efficient, sustainable, and competitive in today's market.",
    initial: "M",
  },
  {
    name: "Jaime Mariano",
    role: "Accountant",
    school: "Bachelor of Science in Accountancy",
    body: "Numbers tell a story, and Jaime helps clients understand it. With experience in accounting, tax compliance, and financial reporting, Jaime ensures businesses have the clarity and confidence needed to make informed decisions and stay on top of their obligations.",
    initial: "JM",
  },
  {
    name: "Jean Ubat",
    role: "Bookkeeper",
    school: "Accounting and Bookkeeping Specialist",
    body: "Focused on accuracy and consistency, Jean keeps financial records clean, organised, and ready for reporting. From coding transactions to reconciling accounts, Jean helps turn day-to-day bookkeeping into reliable financial insights for business owners.",
    initial: "JU",
  },
  {
    name: "Chaelvin Jude",
    role: "Lead Accountant",
    school: "University of Auckland",
    body: "If it involves number crunching, count me in. Recently completed the CPA Australia Program and is now a fully fledged CPA — bringing numerical, analytical and presentation skills to every engagement.",
    initial: "CJ",
  },
  {
    name: "Justine",
    role: "Lead Technology",
    school: "Auckland University of Technology",
    body: "Bachelor of Engineering: Technology in Computer and Mobile Systems Engineering. Builds the systems that turn monthly bookkeeping into clean, automatic tax-position emails.",
    initial: "J",
  },
  {
    name: "Gerard Emmanuel",
    role: "Lead Design",
    school: "Media Design School, Auckland",
    body: "Technical Director with a history in the movie industry. Bachelor of Art and Design focused in 3D Animation and VFX. Brings cinematic clarity to financial reports clients actually want to read.",
    initial: "GE",
  },
];

export default function About() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <main className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid="about-page">
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Hero / Founder narrative */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="about-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back to homepage
          </Link>

          <div className="mt-8 grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-xs uppercase tracking-[0.22em] font-bold text-slate-700 bg-white/60 backdrop-blur"
              >
                <Sparkles className="w-3 h-3 text-[#C9A349]" />
                The team behind NZ Taxation
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mt-6 font-heading font-black text-5xl sm:text-6xl tracking-tight leading-[0.95]"
                data-testid="about-headline"
              >
                Accountants who've <br />
                <span className="text-[#C9A349]">run a business</span> too.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.12 }}
                className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed"
              >
                We're a fully remote NZ tax practice — accountants, technologists and designers
                working together so that hardworking sole traders and small companies stay
                compliant, pay only the tax they owe, and actually understand their numbers.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="md:col-span-5"
            >
              <div className="relative">
                <div className="absolute -inset-6 bg-[#C9A349]/15 blur-3xl pointer-events-none" />
                <figure className="relative bg-[#0A192F] text-white p-8 md:p-10">
                  <Quote className="w-8 h-8 text-[#C9A349]" />
                  <blockquote className="mt-5 font-heading font-bold text-xl md:text-2xl leading-snug">
                    "Most accountants only look at your numbers after the year is finished. I'd
                    rather work with you during the year — keeping things organised so there are no
                    nasty tax surprises."
                  </blockquote>
                  <figcaption className="mt-7 border-t border-white/15 pt-4 flex items-center gap-3">
                    <Avatar initial="M" tone="dark" size="md" />
                    <div className="leading-tight">
                      <div className="font-heading font-bold">Michael</div>
                      <div className="text-xs text-slate-300 uppercase tracking-[0.18em]">
                        Founder & Director
                      </div>
                    </div>
                  </figcaption>
                </figure>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder long-form */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            About the founder
          </div>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-4xl tracking-tight">
            Hi, I'm Michael — the founder of NZ Taxation.
          </h2>
          <div className="mt-8 space-y-6 text-slate-700 text-lg leading-relaxed font-body">
            <p>
              I'm not your typical accountant who has only ever worked behind a desk in an
              accounting office. Before building this practice, I was a businessman myself. I've
              experienced the real side of running a business — the pressure of cashflow, making
              tough decisions, taking risks, and yes… even failing.
            </p>
            <p>
              One of my earlier ventures didn't work out. And while failure is never easy, it
              taught me something no textbook or accounting degree ever could: what it actually
              feels like to run a business when everything is on the line.
            </p>
            <p className="border-l-2 border-[#C9A349] pl-6 italic text-slate-800">
              That experience shaped how I work with my clients today. When tradies and sole
              traders come to me, they're not just looking for someone to file their taxes once a
              year. They want someone who understands how hard they work, how confusing taxes can
              be, and how important it is to keep more of what they earn.
            </p>
            <p>
              At NZ Taxation, my focus is simple: help hardworking business owners stay compliant,
              minimise taxes legally, and understand their numbers so they can make better
              business decisions. Good accounting isn't just about reporting the past — it's about
              helping you build a better future for your business and your family.
            </p>
            <p className="font-heading font-bold text-xl text-[#0A192F]">
              If you're a tradie, sole trader, or small-company owner who wants practical advice
              from someone who understands business beyond spreadsheets, you're in the right
              place.
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            Our values
          </div>
          <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            What we're <span className="text-[#C9A349]">building</span> — and why.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <ValueCard
              icon={Compass}
              kicker="Vision"
              title="Lasting financial freedom — for everyone."
              body="A world where every person, regardless of background, has the tools, knowledge, and support to achieve lasting financial freedom."
              accent
            />
            <ValueCard
              icon={Sparkles}
              kicker="Mission"
              title="Tax & budgeting, made human."
              body="To eliminate financial struggle by delivering accessible, culturally-relevant, and tech-powered budgeting and tax solutions — empowering individuals and families to take control of their money, build wealth, and live with dignity."
            />
          </div>
        </div>
      </section>

      {/* Team grid */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
                Our team
              </div>
              <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tight leading-tight">
                Accounting, technology, design — <br />
                under one roof.
              </h2>
              <p className="mt-4 text-slate-600 max-w-2xl">
                A balanced team is what makes a modern practice work. Numbers we can trust, systems
                that don't break, and reports you actually want to read.
              </p>
            </div>
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#0A192F] hover:text-[#C9A349] transition-colors"
            >
              See plans <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {TEAM.map((m, i) => (
              <motion.article
                key={m.name}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="border border-slate-200 bg-white p-7 md:p-9 flex gap-6 items-start hover:-translate-y-1 transition-transform"
                data-testid={`team-card-${m.initial.toLowerCase()}`}
              >
                <Avatar initial={m.initial} size="lg" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
                    {m.role}
                  </div>
                  <h3 className="mt-1 font-heading font-black text-2xl md:text-3xl tracking-tight">
                    {m.name}
                  </h3>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                    <GraduationCap className="w-3.5 h-3.5" />
                    {m.school}
                  </div>
                  <p className="mt-4 text-slate-600 leading-relaxed">{m.body}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-[#0A192F] text-white bg-grid-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#C9A349]">
              Talk to us
            </div>
            <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tight leading-tight">
              Got a record-keeping question? <br />
              We're available.
            </h2>
            <p className="mt-5 text-slate-300 max-w-xl">
              Drop us a line — a real human reads every message and we aim to reply within
              one business day.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-6 py-4 font-medium hover:bg-white transition-colors"
              data-testid="about-cta-pricing"
            >
              See monthly plans <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-2 border border-white/30 px-6 py-4 font-medium hover:bg-white/10 transition-colors"
              data-testid="about-cta-email"
            >
              Email the team
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}

function ValueCard({ icon: Icon, kicker, title, body, accent = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`p-8 md:p-10 border ${
        accent
          ? "bg-[#0A192F] text-white border-[#0A192F]"
          : "bg-white border-slate-200"
      }`}
      data-testid={`value-card-${kicker.toLowerCase()}`}
    >
      <Icon className={`w-6 h-6 ${accent ? "text-[#C9A349]" : "text-[#0A192F]"}`} />
      <div
        className={`mt-5 text-[10px] uppercase tracking-[0.22em] font-bold ${
          accent ? "text-[#C9A349]" : "text-slate-500"
        }`}
      >
        {kicker}
      </div>
      <h3
        className={`mt-2 font-heading font-black text-2xl md:text-3xl tracking-tight leading-tight ${
          accent ? "text-white" : "text-[#0A192F]"
        }`}
      >
        {title}
      </h3>
      <p
        className={`mt-4 leading-relaxed ${
          accent ? "text-slate-300" : "text-slate-600"
        }`}
      >
        {body}
      </p>
    </motion.div>
  );
}

function Avatar({ initial, tone = "light", size = "md" }) {
  const dim =
    size === "lg" ? "w-16 h-16 text-xl" : size === "md" ? "w-12 h-12 text-base" : "w-10 h-10 text-sm";
  const palette =
    tone === "dark"
      ? "bg-[#C9A349] text-[#0A192F]"
      : "bg-[#0A192F] text-[#C9A349]";
  return (
    <div
      className={`${dim} ${palette} grid place-items-center font-heading font-black flex-shrink-0`}
      aria-hidden
    >
      {initial}
    </div>
  );
}
